import React, { useState } from 'react';
import { Moon, Sun, Download, Loader2, LogIn, LogOut, Crown, User, Ticket } from 'lucide-react';
import { usePDF } from '../context/PDFContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import DownloadLimitModal from './DownloadLimitModal';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Header = () => {
  const { isDarkMode, toggleDarkMode, pdfInfo, resetPDF, refreshLock } = usePDF();
  const { user, logout, loading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const isPro = user?.is_pro || user?.plan === 'lifetime' || user?.plan === 'monthly' || user?.is_staff;
  const isStaff = !!user?.is_staff;
  const credits = user?.remaining_downloads || 0;

  const handleDownload = async () => {
    if (!pdfInfo) return;

    // Check download limit first
    try {
      const checkRes = await axios.get(`${API}/downloads/check`, { withCredentials: true });
      if (!checkRes.data.allowed) {
        setShowLimitModal(true);
        return;
      }
    } catch {
      // If check fails, allow download attempt (backend will enforce)
    }

    setIsDownloading(true);
    toast.loading('Preparing download...', { id: 'download' });

    try {
      const response = await axios.get(`${API}/download/${pdfInfo.id}`, {
        responseType: 'blob',
        withCredentials: true,
        onDownloadProgress: (e) => {
          if (e.total) {
            const pct = Math.round((e.loaded * 100) / e.total);
            toast.loading(`Downloading... ${pct}%`, { id: 'download' });
          }
        },
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const filename = pdfInfo.filename || 'edited.pdf';

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        setTimeout(() => { window.open(url, '_blank'); }, 100);
        toast.success('PDF ready! Opening in new tab...', { id: 'download', duration: 4000 });
      } else {
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);
        toast.success('PDF downloaded successfully!', { id: 'download' });
      }
      if (user) refreshUser();
      refreshLock();
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 402) {
        setShowLimitModal(true);
        toast.dismiss('download');
      } else {
        toast.error('Download failed. Please try again.', { id: 'download' });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleHome = () => {
    resetPDF();
    navigate('/');
  };

  return (
    <>
      <header data-testid="app-header" className="h-14 border-b border-border flex items-center px-4 justify-between bg-surface z-50">
        <div className="flex items-center gap-3">
          <button onClick={handleHome} className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="home-button">
            <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-sm">PS</span>
            </div>
            <h1 className="text-lg font-bold font-heading tracking-tight hidden sm:block">PDF Studio Pro</h1>
          </button>
        </div>

        <div className="flex items-center gap-2">
          {pdfInfo && (
            <Button data-testid="download-button" onClick={handleDownload} size="sm" className="h-10 sm:h-9 rounded-md active:scale-95 transition-transform px-3" disabled={isDownloading}>
              {isDownloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
              {isDownloading ? 'Saving...' : 'Download'}
            </Button>
          )}

          {user && !isPro && credits > 0 && (
            <span
              className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold bg-blue-500/10 text-blue-600 dark:text-blue-400 px-2.5 py-1 rounded-full"
              data-testid="credits-badge"
              title="Purchased download credits"
            >
              <Ticket className="w-3.5 h-3.5" /> {credits} credit{credits > 1 ? 's' : ''}
            </span>
          )}

          {!isPro && (
            <Button
              data-testid="upgrade-header-btn"
              variant="outline"
              size="sm"
              className="h-10 sm:h-9 px-3 rounded-md text-primary border-primary/30 hover:bg-primary/5"
              onClick={() => navigate('/pricing')}
            >
              <Crown className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Pro</span>
            </Button>
          )}

          {!authLoading && (
            user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="w-9 h-9 rounded-full overflow-hidden border-2 border-border hover:border-primary/50 transition-colors"
                  data-testid="user-avatar-btn"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-primary/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                  )}
                </button>
                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 top-12 w-56 bg-card border border-border rounded-lg shadow-lg z-50 py-2" data-testid="user-dropdown">
                      <div className="px-3 py-2 border-b border-border">
                        <p className="text-sm font-medium truncate">{user.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        {isPro && (
                          <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                            <Crown className="w-3 h-3" /> {isStaff ? 'STAFF' : 'PRO'}
                          </span>
                        )}
                      </div>
                      {!isPro && (
                        <button
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
                          onClick={() => { setShowUserMenu(false); navigate('/pricing'); }}
                        >
                          <Crown className="w-4 h-4" /> Upgrade to Pro
                        </button>
                      )}
                      <button
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
                        onClick={() => { setShowUserMenu(false); navigate('/account'); }}
                        data-testid="account-menu-btn"
                      >
                        <User className="w-4 h-4" /> Account &amp; Billing
                      </button>
                      <button
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 text-red-500"
                        onClick={() => { setShowUserMenu(false); logout(); }}
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-4 h-4" /> Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Button data-testid="login-btn" onClick={() => navigate('/login')} variant="ghost" size="sm" className="h-10 sm:h-9 px-3 rounded-md">
                <LogIn className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">Sign in</span>
              </Button>
            )
          )}

          <Button data-testid="theme-toggle" onClick={toggleDarkMode} variant="outline" size="sm" className="w-10 h-10 sm:w-9 sm:h-9 p-0 rounded-md active:scale-95 transition-transform">
            {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
        </div>
      </header>

      <DownloadLimitModal open={showLimitModal} onClose={() => setShowLimitModal(false)} />
    </>
  );
};

export default Header;
